import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Card,
  CircularProgress,
  Container,
  Grid,
  Typography
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { UserContext } from 'src/contexts/userContext';
import { TimeSpan, UnixTime } from 'src/dataCache/time';
import { FetchResult } from 'src/dataCache/dataCache';
import { JSONRecordData } from 'src/content/dashboards/Log/graph.util';
import { FormattedMessage } from 'react-intl';
import { fetchDataJson } from 'src/content/dashboards/Installations/fetchData';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import InformationSalidomo from '../Information/InformationSalidomo';
import BatteryViewSalidomo from '../BatteryView/BatteryViewSalidomo';
import Log from '../Log/Log';
import CancelIcon from '@mui/icons-material/Cancel';
import SalidomoOverview from '../Overview/salidomoOverview';
import { UserType } from '../../../interfaces/UserTypes';
import { PARTNER_TICKETS_ENABLED } from '../../../config/featureFlags';
import HistoryOfActions from '../History/History';
import BuildIcon from '@mui/icons-material/Build';
import InstallationTicketsTab from '../Tickets/InstallationTicketsTab';
import DocumentsTab from '../Documents/DocumentsTab';
import AccessContextProvider from '../../../contexts/AccessContextProvider';
import Access from '../ManageAccess/Access';

interface singleInstallationProps {
  current_installation?: I_Installation;
  type?: string;
}

function SalidomoInstallation(props: singleInstallationProps) {
  const context = useContext(UserContext);
  const { currentUser } = context;
  const location = useLocation().pathname;
  const [errorLoadingS3Data, setErrorLoadingS3Data] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>(undefined);
  const [values, setValues] = useState<JSONRecordData | null>(null);
  const status = props.current_installation.status;
  const [
    failedToCommunicateWithInstallation,
    setFailedToCommunicateWithInstallation
  ] = useState(0);
  const [connected, setConnected] = useState(true);
  const [loading, setLoading] = useState(true);

  if (props.current_installation == undefined) {
    return null;
  }

  const S3data = {
    s3Region: props.current_installation.s3Region,
    s3Provider: props.current_installation.s3Provider,
    s3Key: props.current_installation.s3Key,
    s3Secret: props.current_installation.s3Secret,
    s3BucketId: props.current_installation.s3BucketId
  };

  const s3Bucket =
    props.current_installation.s3BucketId.toString() +
    '-' +
    'c0436b6a-d276-4cd8-9c44-1eae86cf5d0e';

  const [fetchFunctionCalled, setFetchFunctionCalled] = useState(false);
  const s3Credentials = { s3Bucket, ...S3data };

  function timeout(delay: number) {
    return new Promise((res) => setTimeout(res, delay));
  }

  const continueFetching = useRef(false);

  const fetchDataPeriodically = async () => {
    var timeperiodToSearch = 30;
    let res: FetchResult<Record<string, JSONRecordData>> | undefined;
    let timestampToFetch;

    for (var i = 0; i < timeperiodToSearch; i += 1) {
      if (!continueFetching.current) {
        return false;
      }
      timestampToFetch = UnixTime.now().earlier(TimeSpan.fromMinutes(i));
      console.log('timestamp to fetch is ' + timestampToFetch);

      try {
        res = await fetchDataJson(timestampToFetch, s3Credentials, true);
        if (res !== FetchResult.notAvailable && res !== FetchResult.tryLater) {
          break;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        return false;
      }
    }

    if (i >= timeperiodToSearch) {
      setConnected(false);
      setLoading(false);
      return false;
    }
    setConnected(true);
    setLoading(false);
    console.log('NUMBER OF FILES=' + Object.keys(res).length);

    console.log('res= [S3 credentials hidden]');

    while (continueFetching.current) {
      for (const timestamp of Object.keys(res)) {
        if (!continueFetching.current) {
          setFetchFunctionCalled(false);
          return false;
        }
        console.log(`Timestamp: ${timestamp}`);
        const { Config: { S3: { Key, Secret, ...s3Rest } = {} as any, ...configRest } = {} as any, ...dataRest } = res[timestamp] || {};
        console.log('object is', { ...dataRest, Config: { ...configRest, S3: { ...s3Rest, Key: '***', Secret: '***' } } });

        // Set values asynchronously with delay
        setValues(res[timestamp]);
        // setValues(
        //   extractValues({
        //     time: UnixTime.fromTicks(parseInt(timestamp, 10)),
        //     value: res[timestamp]
        //   })
        // );
        // Wait for 2 seconds before processing next timestamp
        await timeout(2000);
      }

      timestampToFetch = timestampToFetch.later(TimeSpan.fromMinutes(20));
      console.log('NEW TIMESTAMP TO FETCH IS ' + timestampToFetch);

      for (i = 0; i < 10; i++) {
        if (!continueFetching.current) {
          return false;
        }

        try {
          console.log('Trying to fetch timestamp ' + timestampToFetch);
          res = await fetchDataJson(timestampToFetch, s3Credentials, true);
          if (
            res !== FetchResult.notAvailable &&
            res !== FetchResult.tryLater
          ) {
            break;
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          return false;
        }
        timestampToFetch = timestampToFetch.later(TimeSpan.fromMinutes(1));
      }
    }
  };
  useEffect(() => {
    let path = location.split('/');
    setCurrentTab(path[path.length - 1]);
  }, [location]);

  const fetchDataForOneTime = async () => {
    var timeperiodToSearch = 30;
    for (var i = 0; i < timeperiodToSearch; i += 1) {
      var timestampToFetch = UnixTime.now().earlier(TimeSpan.fromMinutes(i));
      try {
        var res = await fetchDataJson(timestampToFetch, s3Credentials, true);
        if (res !== FetchResult.notAvailable && res !== FetchResult.tryLater) {
          setConnected(true);
          setLoading(false);
          return true;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setConnected(false);
        setLoading(false);
        return false;
      }
    }
    setConnected(false);
    setLoading(false);
    return false;
  };

  useEffect(() => {
    if (location.includes('batteryview') || currentTab == 'overview') {
      if (location.includes('batteryview') && !location.includes('mainstats')) {
        if (!continueFetching.current) {
          continueFetching.current = true;
          if (!fetchFunctionCalled) {
            setFetchFunctionCalled(true);
            fetchDataPeriodically();
          }
        }
      }
      // Fetch one time in overview tab to determine connectivity
      if (currentTab == 'overview') {
        fetchDataForOneTime();
      }

      return () => {
        continueFetching.current = false;
      };
    } else {
      continueFetching.current = false;
    }
  }, [currentTab, location]);

  useEffect(() => {
    if (status === null) {
      setConnected(false);
    }
  }, [status]);

  return (
    <>
      <Grid item xs={12} md={12}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{
              marginTop: '-20px',
              marginBottom: '10px',
              fontSize: '14px'
            }}
          >
            <FormattedMessage
              id="installation_name_simple"
              defaultMessage="Installation Name:"
            />
          </Typography>
          <Typography
            fontWeight="bold"
            color="orange"
            noWrap
            sx={{
              marginTop: '-20px',
              marginBottom: '10px',
              marginLeft: '5px',
              fontSize: '14px'
            }}
          >
            {props.current_installation.name}
          </Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{
              marginTop: '0px',
              marginBottom: '10px',
              fontSize: '14px'
            }}
          >
            Status:
          </Typography>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '75px',
              marginTop: '-10px'
            }}
          >
            {status === -1 ? (
              <CancelIcon
                style={{
                  width: '23px',
                  height: '23px',
                  color: 'red',
                  borderRadius: '50%'
                }}
              />
            ) : (
              ''
            )}

            {status === -2 ? (
              <CircularProgress
                size={20}
                sx={{
                  color: '#f7b34d'
                }}
              />
            ) : (
              ''
            )}

            <div
              style={{
                width: '20px',
                height: '20px',
                marginLeft: '2px',
                borderRadius: '50%',
                backgroundColor:
                  status === 2
                    ? 'red'
                    : status === 1
                    ? 'orange'
                    : status === -1 || status === -2
                    ? 'transparent'
                    : 'green'
              }}
            />

            {props.current_installation.testingMode && (
              <BuildIcon
                style={{
                  width: '23px',
                  height: '23px',
                  color: 'purple',
                  borderRadius: '50%',
                  position: 'relative',
                  zIndex: 1,
                  marginLeft: '15px'
                }}
              />
            )}
          </div>
        </div>
        {loading &&
          currentTab != 'information' &&
          // currentTab != 'manage' &&
          currentTab != 'history' &&
          currentTab != 'log' &&
          currentTab != 'installationTickets' && (
            <Container
              maxWidth="xl"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '70vh'
              }}
            >
              <CircularProgress size={60} style={{ color: '#ffc04d' }} />
              <Typography
                variant="body2"
                style={{ color: 'black', fontWeight: 'bold' }}
                mt={2}
              >
                <FormattedMessage id="connectingToDevice" defaultMessage="Connecting to the device..." />
              </Typography>
            </Container>
          )}

        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.information}
                element={
                  <InformationSalidomo
                    values={props.current_installation}
                    s3Credentials={s3Credentials}
                    type={props.type}
                  ></InformationSalidomo>
                }
              />

              <Route
                path={routes.log}
                element={
                  <Log
                    errorLoadingS3Data={errorLoadingS3Data}
                    id={props.current_installation.id}
                    status={props.current_installation.status}
                  ></Log>
                }
              />

              <Route
                path={routes.overview}
                element={
                  <SalidomoOverview
                    s3Credentials={s3Credentials}
                    id={props.current_installation.id}
                    connected={connected}
                    loading={loading}
                  ></SalidomoOverview>
                }
              />

              <Route
                path={routes.batteryview + '/*'}
                element={
                  <BatteryViewSalidomo
                    values={values}
                    s3Credentials={s3Credentials}
                    installationId={props.current_installation.id}
                    productNum={props.current_installation.product}
                    connected={connected}
                  ></BatteryViewSalidomo>
                }
              ></Route>

              {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.history}
                  element={
                    <HistoryOfActions
                      errorLoadingS3Data={errorLoadingS3Data}
                      id={props.current_installation.id}
                    ></HistoryOfActions>
                  }
                />
              )}

              {/* {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.manage}
                  element={
                    <AccessContextProvider>
                      <Access
                        currentResource={props.current_installation}
                        resourceType={props.type}
                      ></Access>
                    </AccessContextProvider>
                  }
                />
              )} */}

              {(currentUser.userType == UserType.admin ||
                PARTNER_TICKETS_ENABLED) && (
                <Route
                  path={routes.installationTickets}
                  element={
                    <InstallationTicketsTab
                      installationId={props.current_installation.id}
                    />
                  }
                />
              )}

              {/* Documents tab is open to every role: each sees the installation's
                  files and may delete the ones they uploaded themselves. */}
              {(currentUser.userType == UserType.admin ||
                currentUser.userType == UserType.partner ||
                currentUser.userType == UserType.client) && (
                <Route
                  path={routes.documents}
                  element={
                    <DocumentsTab
                      installationId={props.current_installation.id}
                      product={props.current_installation.product}
                    />
                  }
                />
              )}

              <Route
                path={'*'}
                element={<Navigate to={routes.batteryview}></Navigate>}
              />
            </Routes>
          </Grid>
        </Card>
      </Grid>
    </>
  );
}

export default SalidomoInstallation;
